import {Component, forwardRef, Input, ViewChild} from '@angular/core';
import {CountryList, StateList} from '@service/enum/process-state.enum';
import {isValid} from 'driver-license-validator/dist';
import * as moment from 'moment/moment';
import {AppConfigService} from '@service/config/app-config.service';
import {ApplicantModel} from '@service/models';
import {AbstractControl, ControlValueAccessor, NG_VALIDATORS, NG_VALUE_ACCESSOR, NgForm, ValidationErrors} from '@angular/forms';
import {DateComponent} from '@shared/form-components/date/date.component';
import {getInstitutionId} from '../../../constants/constant';
import {environment} from '@env/environment';
import {SharedService} from '@core/util-services/shared.service';

@Component({
    selector: 'cs-identity-info',
    templateUrl: './identity-info.component.html',
    styleUrls: ['./identity-info.component.scss'],
    providers: [{
        provide: NG_VALUE_ACCESSOR,
        useExisting: forwardRef(() => IdentityInfoComponent),
        multi: true
    }, {
        provide: NG_VALIDATORS,
        useExisting: forwardRef(() => IdentityInfoComponent),
        multi: true
    }]
})

export class IdentityInfoComponent implements ControlValueAccessor {
    @ViewChild(NgForm) f: NgForm;
    @ViewChild('idtIssueDate') idtIssueDate: DateComponent;
    @ViewChild('idtExpDate') idtExpDate: DateComponent;
    @Input('data-test-id') testId: string = 'identity-info';
    protected readonly countryList = CountryList;
    protected readonly drivingLicenceStateList = StateList;
    appConfig = AppConfigService.config;
    // ngModel writes the value in a microtask, so the template renders once before writeValue runs
    applicant: ApplicantModel = new ApplicantModel();

    todayDate: Date = new Date();
    tomorrowDate: Date = new Date();
    yesterdayDate: Date = new Date();
    maxIDExpDate: Date = moment().add(99, 'years').toDate();
    institutionSettings = getInstitutionId(environment.env);
    cssWidthClass = this.institutionSettings.singleColumnLayout ? 'col-sm-12' : 'col-md-6';

    identityTypeList = [
        {
            value: 'USPassport',
            name: 'US Passport'
        },
        {
            value: 'Passport',
            name: 'International Passport'
        },
        {
            value: 'militaryID',
            name: 'Military ID'
        },
        {
            value: 'stateID',
            name: 'State ID'
        },
        {
            value: 'drivingLicence',
            name: 'Driving License'
        }
    ];

    constructor(public sharedService: SharedService) {
        if (this.appConfig.identityTypeList) {
            this.identityTypeList = this.appConfig.identityTypeList;
        }
        this.tomorrowDate.setDate(this.todayDate.getDate() + 1);
        this.yesterdayDate.setDate(this.todayDate.getDate() - 1);
    }

    validate(control: AbstractControl): ValidationErrors | null {
        return this.f?.form.invalid ? {invalid: true} : null;
    }

    runValidation() {
        this.f.form.markAllAsTouched();
        this.idtExpDate?.touch();
        this.idtIssueDate?.touch();
    }

    registerOnChange(fn: any): void {
    }

    registerOnTouched(fn: any): void {
    }

    writeValue(value: ApplicantModel): void {
        if (!value) {
            return;
        }

        if (value.idType === 'USPassport') {
            value.idIssuer = 'USA';
        }

        /* Convert into a date object so can display in Date picker*/
        if (typeof value.idExpDate === 'string') {
            value.idExpDate = new Date(value.idExpDate);
        }
        if (typeof value.idIssueDate === 'string') {
            value.idIssueDate = new Date(value.idIssueDate);
        }

        this.applicant = value || new ApplicantModel();

        if (this.applicant && this.sharedService.calculateAge(this.applicant.dob) > 65 ) {
            this.maxIDExpDate.setFullYear(this.maxIDExpDate.getFullYear() + 9999);
        } else {
            this.maxIDExpDate.setFullYear(this.maxIDExpDate.getFullYear() + 99);
        }
    }

    validateDriversLicense(elRef: any, stateValue = null) {
        elRef.control.setErrors(null);
        try {
            const options: any = {
                country: 'US', ignoreCase: true,
                ...(stateValue ? {states: stateValue} : {})
            };
            if (!isValid(elRef.control.value, options)) {
                elRef.control.setErrors({'driverLicense': true});
            }
        } catch (e) {
            elRef.control.setErrors({'driverLicense': true});
        }
    }

}
